import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';
import { token } from '@atlaskit/tokens';

import {
	useSetOnboardingState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useContentType } from '@confluence/page-context';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import {
	IS_DISMISSED_FLAG,
	MessageType,
	READ_MORE_URL,
	REDACTIONS_TOUR_MESSAGE_IDS,
	REDACTIONS_TOUR_TARGETS,
	RedactionTourStepSource,
	RedactionsTourSteps,
} from '../redaction-constants.ts';

import { useRedactionsTourState } from './useRedactionsTourState.ts';

const { IS_REDACTIONS_TOUR_VISIBLE } = ONBOARDING_EXPERIENCES;

const i18n = defineMessages({
	stepOfSteps: {
		id: 'redactions.redactions-tour.steps',
		defaultMessage: '{step}/{steps}',
		description: 'A label that tracks which step of the tour the user is currently on',
	},
	dismiss: {
		id: 'redactions.redactions-tour.dismiss',
		defaultMessage: 'Dismiss',
		description: 'A label that indicates which button should be clicked to dismiss the tour',
	},
	next: {
		id: 'redactions.redactions-tour.next',
		defaultMessage: 'Next',
		description:
			'A label that indicates which button should be clicked to go to the next step of the tour',
	},
	ok: {
		id: 'redactions.redactions-tour.ok',
		defaultMessage: 'OK',
		description: 'A label that indicates which button should be clicked to end the tour',
	},
	readMore: {
		id: 'redactions.redactions-tour.read-more',
		defaultMessage: 'Read more',
		description: 'A label that indicates which button should be clicked to read more',
	},
	solidBarIndicates: {
		id: 'redactions.redactions-tour.solid-bar-indicates',
		defaultMessage:
			'The solid bar indicates that your organization detected and deleted sensitive data.',
		description: 'The text of a spotlight highlighting the nature of the redactions feature',
	},
	goToRedactions: {
		id: 'redactions.redactions-tour.go-to-redactions',
		defaultMessage:
			'Go to Redactions in this menu to view details about the redactions on this page.',
		description:
			'The text of a spotlight showing the user where they can see more about the redactions',
	},
	aboutRedactions: {
		id: 'redactions.redactions-tour.about-redactions',
		defaultMessage: 'About redactions',
		description: 'The heading of the spotlight that explains redactions in the page content',
	},
	viewRedactionDetails: {
		id: 'redactions.redactions-tour.view-redaction-details',
		defaultMessage: 'View redaction details',
		description: 'The heading of the spotlight that points to the details panel',
	},
});

type UseSpotlightsProps = {
	next: () => void;
	end: () => void;
	tourStartsInTitle?: boolean;
};

const useSpotlights = ({ next, end, tourStartsInTitle }: UseSpotlightsProps) => {
	const { formatMessage } = useIntl();

	const firstSpotlightKeyTarget = tourStartsInTitle
		? REDACTIONS_TOUR_TARGETS.TITLE_REDACTION
		: REDACTIONS_TOUR_TARGETS.REDACTION_EXTENSION;

	return [
		{
			key: firstSpotlightKeyTarget,
			messageId: REDACTIONS_TOUR_MESSAGE_IDS.REDACTION,
			target: firstSpotlightKeyTarget,
			messageType: MessageType.Engagement,
			heading: formatMessage(i18n.aboutRedactions),
			actionsBeforeElement: formatMessage(i18n.stepOfSteps, { step: 1, steps: 2 }),
			targetRadius: 3,
			actions: [
				{ text: formatMessage(i18n.dismiss), onClick: end },
				{ text: formatMessage(i18n.next), onClick: next },
			],
			children: formatMessage(i18n.solidBarIndicates),
		},
		{
			key: REDACTIONS_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON,
			messageId: REDACTIONS_TOUR_MESSAGE_IDS.DETAILS_BUTTON,
			target: REDACTIONS_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON,
			messageType: MessageType.Transactional,
			heading: formatMessage(i18n.viewRedactionDetails),
			actionsBeforeElement: formatMessage(i18n.stepOfSteps, { step: 2, steps: 2 }),
			targetRadius: 100,
			actions: [{ text: formatMessage(i18n.ok), onClick: end }],
			children: (
				<>
					{formatMessage(i18n.goToRedactions)}{' '}
					<Link href={READ_MORE_URL} target="_blank" rel="noopener noreferrer">
						{formatMessage(i18n.readMore)}
					</Link>
				</>
			),
		},
	];
};

type UseRedactionsTourProps = {
	contentId?: string | null;
	step: number;
	firstStepSource?: RedactionTourStepSource;
};

export const useRedactionsTour = ({
	contentId,
	step,
	firstStepSource = RedactionTourStepSource.bodyRedaction,
}: UseRedactionsTourProps): {
	activeSpotlight: React.JSX.Element | null;
	initiateTour: () => void;
	proceedToNext: () => void;
	concludeTour: () => void;
	resetTour: () => void;
	renderMigratedSpotlight: (target: React.ReactNode) => React.JSX.Element;
	activeSpotlightIndex: number | null;
	lastActiveSpotlightIndex: number | null;
	isTourCompleted: boolean | undefined;
	setFirstRedactionIdRendered: (redactionId: string, contentId?: string | null) => void;
	setTitleRedactionRenderedForContentId: (contentId: string | null) => void;
} => {
	const { sidebarControlState } = useObjectSidebarState();
	const [
		{
			activeSpotlightIndex,
			lastActiveSpotlightIndex,
			firstRedactionIdRendered,
			firstRedactionRenderedForContentId,
			titleRedactionRenderedForContentId,
			firstStepSource: activeFirstStepSource,
		},
		{
			concludeTour: endTour,
			proceedToNext,
			initiateTour,
			resetTour,
			setFirstRedactionIdRendered,
			setTitleRedactionRenderedForContentId,
		},
	] = useRedactionsTourState();

	const isFirstTourTargetVisible =
		firstStepSource === RedactionTourStepSource.bodyRedaction
			? step === 0 &&
				firstRedactionIdRendered &&
				firstRedactionRenderedForContentId === contentId &&
				titleRedactionRenderedForContentId !== contentId
			: step === 0 && titleRedactionRenderedForContentId === contentId; // otherwise, it's a title redaction

	const isSecondTourTargetVisible = sidebarControlState === 'shown';

	const [contentType] = useContentType();
	const canUpdateContent = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType,
		operation: OperationTypes.UPDATE,
	});
	const isLicensed = useIsLicensed();

	const { setOnboardingState } = useSetOnboardingState();
	const {
		data: onboardingStateData,
		error: onboardingStateError,
		loading: onboardingStateLoading,
	} = useGetOnboardingState(IS_REDACTIONS_TOUR_VISIBLE, false);

	const isTourOnboardingStateCompleted = onboardingStateData?.onboardingState?.some(
		({ key, value }: { readonly key: string; readonly value: string | null | undefined }) => {
			return key === IS_REDACTIONS_TOUR_VISIBLE && value === IS_DISMISSED_FLAG;
		},
	);

	const isTourCompleted =
		!onboardingStateError && !onboardingStateLoading && isTourOnboardingStateCompleted;

	const { Spotlight, SpotlightTarget } = useChoreographerAssets();

	const concludeTour = useCallback((): void => {
		endTour();
		setOnboardingState({ key: IS_REDACTIONS_TOUR_VISIBLE, value: IS_DISMISSED_FLAG });
	}, [endTour, setOnboardingState]);

	const initiateTourForSource = useCallback((): void => {
		initiateTour(firstStepSource);
	}, [firstStepSource, initiateTour]);

	useEffect(() => {
		if (
			activeSpotlightIndex === null &&
			isFirstTourTargetVisible &&
			isSecondTourTargetVisible &&
			!isTourCompleted &&
			!onboardingStateError &&
			!onboardingStateLoading &&
			isLicensed &&
			canUpdateContent
		) {
			const timeoutId = setTimeout(() => {
				initiateTourForSource();
			}, 0);

			return () => clearTimeout(timeoutId);
		}

		return undefined;
	}, [
		activeSpotlightIndex,
		initiateTourForSource,
		isFirstTourTargetVisible,
		isSecondTourTargetVisible,
		isTourCompleted,
		onboardingStateError,
		onboardingStateLoading,
		isLicensed,
		canUpdateContent,
	]);

	const spotlights = useSpotlights({
		next: proceedToNext,
		end: concludeTour,
		tourStartsInTitle: firstStepSource === RedactionTourStepSource.titleRedaction,
	});
	const spotlight = spotlights[step];
	const isSpotlightVisible =
		activeSpotlightIndex === step &&
		(step !== RedactionsTourSteps.FIRST || activeFirstStepSource === firstStepSource);
	const renderMigratedSpotlight = useStableCallback(
		(target: React.ReactNode): React.JSX.Element => (
			<>
				<SpotlightTarget name={spotlight.target}>{target}</SpotlightTarget>
				{isSpotlightVisible ? (
					<Spotlight
						isMigrated
						dialogPlacement="bottom right"
						shouldWatchTarget
						targetBgColor={token('elevation.surface.raised')}
						{...spotlight}
					/>
				) : null}
			</>
		),
	);

	return {
		activeSpotlight: isSpotlightVisible ? (
			<Spotlight
				dialogPlacement="bottom right"
				shouldWatchTarget // Note: problematic layout shifts for both spotlights
				targetBgColor={token('elevation.surface.raised')}
				{...spotlights[activeSpotlightIndex]}
			/>
		) : null,
		initiateTour: initiateTourForSource,
		proceedToNext,
		concludeTour,
		resetTour,
		renderMigratedSpotlight,
		activeSpotlightIndex,
		lastActiveSpotlightIndex,
		isTourCompleted,
		setFirstRedactionIdRendered,
		setTitleRedactionRenderedForContentId, // Note: mainly here for testing purposes
	};
};
